import React, { useMemo, useState, useEffect } from "react";
const SUBMIT_CONFIG = {
  mode: "formsubmit", // <-- use FormSubmit
  formsubmitEndpoint:
    "https://formsubmit.co/sapphirediplomacyleadership@gmail.com",
  apiEndpoint: "/api/careers/apply", // replace if using your own API
  apiUseFormData: true, // true = send multipart/form-data, false = send JSON with base64 CV
  maxFileSizeMB: 5,
};

const ROLES = [
  {
    id: "finance-officer",
    title: "Finance Officer",
    department: "Operations",
    type: "Full time",
    location: "Remote first, Malta or EU time zone preferred",
    openings: 1,
    compensation: "Competitive, commensurate with experience",
    summary:
      "Own statutory and management finance for a small, growing higher education provider. You will lead annual returns, financial statements, payroll, budgets and audit readiness. You will help us build robust financial processes that meet Maltese and EU expectations for a private higher education institution.",
    responsibilities: [
      "Prepare monthly management accounts, cash flow forecasts and board packs by working day five.",
      "Lead statutory year end: financial statements, notes and audit coordination under IFRS or relevant Maltese reporting standards.",
      "Submit annual returns and compliance filings in Malta. Maintain accurate ledgers, chart of accounts and document control.",
      "Run payroll and pensions. Manage expense policy, supplier payments, invoicing and receivables.",
      "Support tuition fee pricing models and scholarship disbursements. Track restricted funds such as scholarship schemes.",
      "Own budget planning, variance analysis and financial risk controls. Maintain a simple but effective internal control framework.",
      "Advise on VAT, cross-border payments and basic tax queries, with escalation to external advisors when needed.",
      "Partner with Quality Assurance and Programme teams to evidence MFHEA-aligned resourcing and sustainability.",
    ],
    requirements: [
      "Degree in Accounting, Finance or related field. A professional qualification (ACCA, CPA or equivalent) is an advantage.",
      "3+ years in a finance role with responsibility for month end and year end.",
      "Payroll experience and strong Excel or Google Sheets skills.",
      "Working knowledge of Xero, Sage or a similar accounting system.",
      "Familiarity with Maltese filings, VAT and audit processes is desirable.",
      "Clear communicator with a process mindset and high attention to detail.",
    ],
    niceToHave: [
      "Experience in higher education, NGOs or professional training organisations.",
      "Set up or improvement of finance processes in a small organisation.",
    ],
    kpis: [
      "On-time monthly close and board pack delivery.",
      "Clean year end with no material audit adjustments.",
      "Payroll accuracy and supplier payment timeliness.",
      "Budget adherence and actionable variance insights.",
    ],
  },
  {
    id: "quality-assurance-officer",
    title: "Quality Assurance Officer",
    department: "Academic Quality",
    type: "Full time",
    location: "Remote first, Malta or EU time zone preferred",
    openings: 1,
    compensation: "Competitive",
    summary:
      "Drive ESBD's quality system across programme design, delivery and review so that it aligns to MFHEA standards and the Guidelines for Online Learning Providers. You will maintain policy suites, lead internal reviews, coordinate evidence for accreditation and embed continuous improvement across modules on our LearnDash platform.",
    responsibilities: [
      "Maintain and improve the Quality Manual, programme and assessment policies, document control and versioning on SharePoint.",
      "Coordinate annual monitoring and programme periodic review. Track actions in our QA tracker and report on progress to management.",
      "Lead internal audits and readiness checks for MFHEA accreditation and follow-up reviews. Curate evidence against the 11 EQA Standards and the online learning Domains.",
      "Quality check modules in LearnDash: contact hours mapping, tutor-moderated forums, MCQs, assignment briefs, accessibility and student information.",
      "Oversee External Examiner processes, assessment moderation and academic integrity controls.",
      "Run student feedback cycles and close the loop with action plans and communications back to learners.",
      "Support staff training on assessment, feedback, accessibility, data protection and complaints and appeals handling.",
      "Work with Marketing on compliant use of awards, accreditation statements and claims on the website and materials.",
    ],
    requirements: [
      "Experience in academic quality assurance in higher education or a regulated training environment.",
      "Sound knowledge of European or Maltese QA frameworks and online learning standards.",
      "Strong policy writing, analysis and evidence curation skills.",
      "Confidence with LMS tools such as LearnDash and collaboration platforms like SharePoint.",
      "Excellent written English and a calm, organised approach to deadlines.",
    ],
    niceToHave: [
      "Experience preparing or supporting MFHEA accreditation.",
      "Familiarity with workload models, contact hour calculations and accessibility standards.",
    ],
    kpis: [
      "On-time completion of monitoring and review cycles.",
      "Closed actions from audits with evidence of improvement.",
      "Module checks that meet contact, assessment and accessibility requirements.",
      "Clear, comprehensible student-facing information and timely responses to feedback.",
    ],
  },
  {
    id: "partnership-officer",
    title: "Partnership Officer",
    department: "Global Partnerships",
    type: "Full time",
    location: "Remote, with periodic travel",
    openings: 2,
    compensation: "Competitive",
    summary:
      "Build academic partnerships that expand ESBD's global footprint. You will source, evaluate and deliver dual qualifications, summer schools, articulation and study abroad opportunities with reputable universities and providers.",
    responsibilities: [
      "Map target institutions and craft value propositions for dual awards and immersion offerings.",
      "Run due diligence, programme alignment and credit mapping with academic leads.",
      "Lead MoUs and agreements through legal review and governance approval.",
      "Coordinate pilots, student mobility logistics and launch plans with Marketing and Academic teams.",
      "Maintain a clear pipeline, forecast timelines and report on conversion and revenue.",
      "Represent ESBD at events and virtual meetings. Nurture productive long-term relationships.",
    ],
    requirements: [
      "Experience developing partnerships in higher education, executive education or related sectors.",
      "Confident negotiator with strong written proposals and presentations.",
      "Understanding of credit frameworks, quality implications and student experience.",
      "Organised, persistent and comfortable with international time zones.",
    ],
    niceToHave: [
      "Existing contacts across Europe, Africa or Asia.",
      "Experience running summer schools or study abroad programmes.",
    ],
    kpis: [
      "Signed MoUs progressing to delivery.",
      "Number of active partnerships and student cohorts launched.",
      "Partner satisfaction and renewal rates.",
    ],
  },
  {
    id: "student-recruitment-officer",
    title: "Student Recruitment Officer",
    department: "Recruitment & Outreach",
    type: "Contract",
    location: "Remote, assigned market(s)",
    openings: 10,
    compensation: "Base plus performance incentives",
    summary:
      "Convert interest into enrolments for ESBD programmes. You will run outreach, manage leads, advise applicants and coordinate webinars and events to meet monthly targets.",
    responsibilities: [
      "Generate and qualify leads through events, schools, social media and referrals.",
      "Own the applicant journey from first contact to enrolment, with clear and timely communication.",
      "Log activity in a simple CRM and keep data accurate and current.",
      "Coordinate webinars and information sessions with academic staff and alumni.",
      "Provide market feedback to improve campaigns and webpages.",
      "Work ethically, with accurate information and respect for student choice.",
    ],
    requirements: [
      "Experience in student recruitment, admissions or sales advisory.",
      "Comfortable working to targets with a helpful, supportive tone.",
      "Strong written and spoken English. Additional languages are an advantage.",
      "Organised, reliable and responsive across time zones.",
    ],
    niceToHave: [
      "Experience recruiting for online Master’s programmes.",
      "Local knowledge of one or more of our focus regions.",
    ],
    kpis: [
      "Monthly enquiry to application conversion.",
      "Application to enrolment conversion.",
      "Event attendance and follow-up effectiveness.",
    ],
  },
  {
    id: "regional-director-africa",
    title: "Regional Director, Africa (Commission Based)",
    department: "Global Growth",
    type: "Commission based",
    location: "Africa, remote",
    openings: 1,
    compensation: "Commission structure",
    summary:
      "Own ESBD's regional strategy and growth in Africa. You will build partnerships, manage agent networks and drive enrolment numbers in priority markets.",
    responsibilities: [
      "Create and execute a regional market plan with clear targets and activities.",
      "Recruit, brief and manage local agents and partners, ensuring compliance with ESBD standards.",
      "Secure partnership opportunities with universities, ministries and reputable organisations.",
      "Forecast pipeline and report on performance. Mitigate risks and maintain quality.",
    ],
    requirements: [
      "Senior experience in higher education growth across African markets.",
      "Strong network and proven delivery against recruitment or partnership targets.",
      "Excellent stakeholder management and ethical practice.",
    ],
    niceToHave: ["Experience with dual awards or government relations."],
    kpis: [
      "Enrolments by country and programme.",
      "Signed partnerships and active cohorts.",
      "Agent productivity and compliance.",
    ],
  },
  {
    id: "regional-director-europe",
    title: "Regional Director, Europe (Commission Based)",
    department: "Global Growth",
    type: "Commission based",
    location: "Europe, remote",
    openings: 1,
    compensation: "Commission structure",
    summary:
      "Lead ESBD's growth across Europe with a focus on partnerships and postgraduate recruitment.",
    responsibilities: [
      "Develop market plans and manage key accounts across the region.",
      "Build collaboration with universities and reputable providers for dual awards and short programmes.",
      "Coordinate events and digital campaigns with central teams.",
    ],
    requirements: [
      "Experience in European HE recruitment or partnerships.",
      "Strong communication and negotiation skills.",
    ],
    niceToHave: ["Existing network across EU member states."],
    kpis: [
      "Regional enrolment and revenue.",
      "Number and quality of partnerships launched.",
    ],
  },
  {
    id: "regional-director-asia",
    title: "Regional Director, Asia (Commission Based)",
    department: "Global Growth",
    type: "Commission based",
    location: "Asia, remote",
    openings: 1,
    compensation: "Commission structure",
    summary:
      "Grow ESBD's presence in Asia through targeted partnerships and recruitment management.",
    responsibilities: [
      "Build and deliver a market plan for priority countries.",
      "Secure collaboration with strong institutions and networks.",
      "Guide local Student Recruitment Officers and agents.",
    ],
    requirements: [
      "Proven growth delivery in Asian HE markets.",
      "Relationship builder with a track record of results.",
    ],
    niceToHave: ["Additional Asian language capability."],
    kpis: ["Enrolments, partnerships, event outcomes."],
  },
  // Country Strategy & Recruitment Directors
  ...["Ghana", "Kenya", "South Africa", "Nigeria"].map((country) => ({
    id: `country-director-${country.toLowerCase().replace(/\s/g, "-")}`,
    title: `Country Strategy and Recruitment Director, ${country}`,
    department: "Global Growth",
    type: "Commission based or hybrid (to be confirmed)",
    location: `${country}, remote`,
    openings: 1,
    compensation: "Commission based, details at interview",
    summary: `Lead ESBD's country strategy for ${country}. You will shape market entry, manage partners and deliver enrolment targets with quality and compliance at the centre.`,
    responsibilities: [
      "Develop a country plan with clear channels, partners and timelines.",
      "Build relationships with universities, ministries, professional bodies and reputable agents.",
      "Coordinate on-the-ground activities such as fairs, school visits and information sessions.",
      "Report performance transparently. Propose improvements and ensure ethical recruitment.",
    ],
    requirements: [
      "Significant experience in student recruitment or partnerships within the country.",
      "Strong network and credibility with education stakeholders.",
      "Organised, persuasive and outcomes focused.",
    ],
    niceToHave: [
      "Experience launching new programmes or brands in the market.",
      "Fluency in relevant local languages.",
    ],
    kpis: [
      "Monthly enrolments and offer acceptance.",
      "Partner activations and event ROI.",
      "Data quality and compliance.",
    ],
  })),
];

const DEPARTMENTS = [
  "All",
  ...Array.from(new Set(ROLES.map((r) => r.department))),
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Modal({ open, onClose, children, title }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className="relative mx-4 w-full max-w-3xl rounded-2xl bg-sdbodywhite shadow-xl"
      >
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 id="modal-title" className="text-lg font-semibold">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100 focus:outline-none focus:ring"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

// Simple runtime validation to catch content regressions in ROLES
function validateRoles(roles) {
  try {
    const errors = [];
    const requiredFields = [
      "id",
      "title",
      "department",
      "type",
      "location",
      "openings",
      "summary",
      "responsibilities",
      "requirements",
    ];

    roles.forEach((r) => {
      requiredFields.forEach((f) => {
        if (
          r[f] === undefined ||
          r[f] === null ||
          (typeof r[f] === "string" && !r[f].trim())
        ) {
          errors.push(
            `Role ${r.title || r.id} is missing required field: ${f}`
          );
        }
      });
      if (
        !Array.isArray(r.responsibilities) ||
        r.responsibilities.length === 0
      ) {
        errors.push(`Role ${r.title} must include responsibilities.`);
      }
      if (!Array.isArray(r.requirements) || r.requirements.length === 0) {
        errors.push(`Role ${r.title} must include requirements.`);
      }
    });

    // Specific checks
    const sr = roles.find((r) => r.id === "student-recruitment-officer");
    if (!sr || sr.openings !== 10)
      errors.push("Student Recruitment Officer 'openings' should be 10.");

    const countryIds = ["ghana", "kenya", "south-africa", "nigeria"].map(
      (c) => `country-director-${c}`
    );
    countryIds.forEach((id) => {
      const found = roles.find((r) => r.id === id);
      if (!found) errors.push(`Missing role for ${id}.`);
    });

    if (errors.length) {
      console.error(
        "[EUSBD Careers] Role validation failed:\n" + errors.join("\n")
      );
    } else {
      console.log(
        "[EUSBD Careers] Role validation passed (",
        roles.length,
        "roles )."
      );
    }
  } catch (e) {
    console.error("[EUSBD Careers] Validation error:", e);
  }
}

export default function CareersPage() {
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("All");
  const [activeRole, setActiveRole] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const roles = useMemo(() => {
    return ROLES.flat();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roles.filter((r) => {
      const matchesDept = dept === "All" || r.department === dept;
      const matchesQuery = !q
        ? true
        : [r.title, r.summary, r.department, r.location]
            .join(" ")
            .toLowerCase()
            .includes(q);
      return matchesDept && matchesQuery;
    });
  }, [query, dept, roles]);

  useEffect(() => {
    // Preselect the role when a modal is opened and user clicks Apply now
    if (activeRole) {
      setSelectedRoleId(activeRole.id);
    }
  }, [activeRole]);

  useEffect(() => {
    // Run lightweight tests once on mount
    validateRoles(roles);
    // Basic config checks
    if (
      SUBMIT_CONFIG.mode === "formspree" &&
      !SUBMIT_CONFIG.formspreeEndpoint
    ) {
      console.error(
        "[EUSBD Careers] Formspree mode selected but formspreeEndpoint is empty."
      );
    }
    if (SUBMIT_CONFIG.mode === "api" && !SUBMIT_CONFIG.apiEndpoint) {
      console.error(
        "[EUSBD Careers] API mode selected but apiEndpoint is empty."
      );
    }
  }, [roles]);

  // Unified application form state
  const [form, setForm] = useState({
    roleId: "",
    fullName: "",
    email: "",
    country: "",
    yearsExp: "",
    achievement: "",
    roleSpecific: "",
    cv: null,
  });

  const selectedRole = useMemo(() => {
    return roles.find((r) => r.id === (form.roleId || selectedRoleId));
  }, [roles, form.roleId, selectedRoleId]);

  const roleSpecificPrompt = useMemo(() => {
    if (!selectedRole)
      return "Briefly describe a result you delivered that is relevant to this role.";
    const id = selectedRole.id;
    if (id === "finance-officer")
      return "Tell us about a successful year end you led. What was your role and what improved as a result?";
    if (id === "quality-assurance-officer")
      return "Give an example of a QA improvement you implemented that demonstrably raised standards.";
    if (id === "partnership-officer")
      return "Describe a partnership you initiated and launched. What made it work?";
    if (id === "student-recruitment-officer")
      return "Share a time you met or exceeded recruitment targets. What did you do differently?";
    if (id.startsWith("regional-director"))
      return "Outline a simple 90-day plan for the region you would lead.";
    if (id.startsWith("country-director"))
      return "Outline a simple 90-day plan for this country, including partners you would prioritise.";
    return "Briefly describe a result you delivered that is relevant to this role.";
  }, [selectedRole]);

  function onChange(e) {
    const { name, value, files } = e.target;
    if (name === "cv") {
      const file = files && files[0] ? files[0] : null;
      if (file) {
        const max = SUBMIT_CONFIG.maxFileSizeMB * 1024 * 1024;
        const okExt = ["pdf", "doc", "docx"];
        const ext = file.name.split(".").pop()?.toLowerCase();
        if (file.size > max) {
          setFormError(`CV must be under ${SUBMIT_CONFIG.maxFileSizeMB}MB.`);
          return;
        }
        if (!okExt.includes(ext || "")) {
          setFormError("Please upload a PDF or Word document.");
          return;
        }
      }
      setForm((f) => ({ ...f, cv: file }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  }

  async function handleSubmit(e) {
    // DON'T preventDefault() yet — we might want native submit for FormSubmit
    setFormError("");
    setSubmitted(false);

    const roleId = form.roleId || selectedRoleId;
    const roleTitle = roles.find((r) => r.id === roleId)?.title || roleId;

    // ---- Required fields (including file) ----
    const required = [
      roleId,
      form.fullName,
      form.email,
      form.country,
      form.yearsExp,
      form.achievement,
      form.roleSpecific,
      form.cv, // file required
    ];
    if (required.some((x) => !x || (typeof x === "string" && !x.trim()))) {
      e.preventDefault();
      setFormError("Please complete all fields and upload your CV.");
      return;
    }

    // ---- Client-side file checks ----
    const f = form.cv;
    if (f) {
      const max = (SUBMIT_CONFIG.maxFileSizeMB || 5) * 1024 * 1024;
      const okExt = ["pdf", "doc", "docx"];
      const ext = f.name.split(".").pop()?.toLowerCase();
      if (!okExt.includes(ext || "")) {
        e.preventDefault();
        setFormError("CV must be PDF/DOC/DOCX.");
        return;
      }
      if (f.size > max) {
        e.preventDefault();
        setFormError(`CV must be under ${SUBMIT_CONFIG.maxFileSizeMB || 5}MB.`);
        return;
      }
    }

    // ================================
    // MODE: FormSubmit (native submit)
    // ================================
    if (SUBMIT_CONFIG.mode === "formsubmit") {
      // Let the browser do a native multipart/form-data POST to FormSubmit.
      // (Make sure your <form> has action=, method="POST", encType="multipart/form-data")
      setFormLoading(true);
      return; // DO NOT call preventDefault — allow native submit
    }

    // ================================
    // Otherwise: fetch-based submit
    // ================================
    e.preventDefault(); // now we block the browser and handle via JS

    // Helper only used for JSON branch
    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    try {
      setFormLoading(true);

      let res;
      if (SUBMIT_CONFIG.mode === "formspree") {
        // Will still fail if that Formspree form doesn't allow files.
        const fd = new FormData();
        fd.append("roleId", roleId);
        fd.append("roleTitle", roleTitle);
        fd.append("fullName", form.fullName);
        fd.append("email", form.email);
        fd.append("country", form.country);
        fd.append("yearsExp", String(form.yearsExp));
        fd.append("achievement", form.achievement);
        fd.append("roleSpecific", form.roleSpecific);
        if (form.cv) fd.append("cv", form.cv, form.cv.name);

        res = await fetch(SUBMIT_CONFIG.formspreeEndpoint, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        });
      } else if (SUBMIT_CONFIG.mode === "api") {
        if (SUBMIT_CONFIG.apiUseFormData) {
          const fd = new FormData();
          fd.append("roleId", roleId);
          fd.append("roleTitle", roleTitle);
          fd.append("fullName", form.fullName);
          fd.append("email", form.email);
          fd.append("country", form.country);
          fd.append("yearsExp", String(form.yearsExp));
          fd.append("achievement", form.achievement);
          fd.append("roleSpecific", form.roleSpecific);
          if (form.cv) fd.append("cv", form.cv, form.cv.name);

          res = await fetch(SUBMIT_CONFIG.apiEndpoint, {
            method: "POST",
            body: fd,
          });
        } else {
          const payload = {
            roleId,
            roleTitle,
            fullName: form.fullName,
            email: form.email,
            country: form.country,
            yearsExp: form.yearsExp,
            achievement: form.achievement,
            roleSpecific: form.roleSpecific,
            cv: form.cv ? await toBase64(form.cv) : null,
            ts: new Date().toISOString(),
          };
          res = await fetch(SUBMIT_CONFIG.apiEndpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
        }
      } else {
        throw new Error(`Unknown SUBMIT_CONFIG.mode: ${SUBMIT_CONFIG.mode}`);
      }

      if (!res || !res.ok) {
        const text = res ? await res.text().catch(() => "") : "";
        throw new Error(
          text ||
            (res
              ? `Submission failed with status ${res.status}`
              : "No response")
        );
      }

      setSubmitted(true);
      setFormLoading(false);
      setForm((fPrev) => ({
        ...fPrev,
        fullName: "",
        email: "",
        country: "",
        yearsExp: "",
        achievement: "",
        roleSpecific: "",
        cv: null,
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      setFormError(
        "There was a problem submitting your application. Try again."
      );
      setFormLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900">
      {/* Header */}
      <header className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Careers at ESBD
            </h1>
            <p className="mt-2 max-w-3xl text-slate-600">
              Join a modern, online higher education school based in Malta. Help
              us deliver high quality programmes and partnerships that advance
              global leadership.
            </p>
          </div>
          <a
            href="#apply"
            className="rounded-xl bg-slate-900 px-5 py-3 text-white shadow hover:bg-slate-800 focus:outline-none focus:ring"
          >
            Apply now
          </a>
        </div>
      </header>

      {/* Search + Filters */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl border bg-sdbodywhite p-4 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search roles, locations or departments"
                className="w-full rounded-xl border px-4 py-3 pr-9 focus:outline-none focus:ring"
                aria-label="Search roles"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                🔍
              </span>
            </div>
            <div className="sm:w-64">
              <select
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                value={dept}
                onChange={(e) => setDept(e.target.value)}
                aria-label="Filter by department"
              >
                {DEPARTMENTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-3 text-sm text-slate-500">
            Showing {filtered.length} of {roles.length} roles
          </div>
        </div>
      </section>

      {/* Roles Grid */}
      <section className="mx-auto mt-6 max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {filtered.map((r) => (
            <article
              key={r.id}
              className="group rounded-2xl border bg-sdbodywhite p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold group-hover:underline">
                  {r.title}
                </h2>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
                  {r.department}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">{r.summary}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span className="rounded-lg bg-slate-100 px-2 py-1">
                  {r.type}
                </span>
                <span className="rounded-lg bg-slate-100 px-2 py-1">
                  {r.location}
                </span>
                <span className="rounded-lg bg-slate-100 px-2 py-1">
                  Openings: {r.openings}
                </span>
              </div>
              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setActiveRole(r)}
                  className="rounded-xl border px-4 py-2 hover:bg-slate-50 focus:outline-none focus:ring"
                >
                  View role
                </button>
                <a
                  href="#apply"
                  onClick={() => setSelectedRoleId(r.id)}
                  className="rounded-xl bg-slate-900 px-4 py-2 text-white hover:bg-slate-800 focus:outline-none focus:ring"
                >
                  Apply now
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Role Modal */}
      <Modal
        open={!!activeRole}
        onClose={() => setActiveRole(null)}
        title={activeRole?.title}
      >
        {activeRole && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Info label="Department" value={activeRole.department} />
              <Info label="Type" value={activeRole.type} />
              <Info label="Location" value={activeRole.location} />
              <Info label="Openings" value={String(activeRole.openings)} />
            </div>
            <Section title="Summary">
              <p>{activeRole.summary}</p>
            </Section>
            <Section title="Key responsibilities">
              <ul className="list-disc space-y-2 pl-5">
                {activeRole.responsibilities.map((it, idx) => (
                  <li key={idx}>{it}</li>
                ))}
              </ul>
            </Section>
            <Section title="Essential requirements">
              <ul className="list-disc space-y-2 pl-5">
                {activeRole.requirements.map((it, idx) => (
                  <li key={idx}>{it}</li>
                ))}
              </ul>
            </Section>
            {activeRole.niceToHave?.length ? (
              <Section title="Desirable">
                <ul className="list-disc space-y-2 pl-5">
                  {activeRole.niceToHave.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ul>
              </Section>
            ) : null}
            {activeRole.kpis?.length ? (
              <Section title="Success measures">
                <ul className="list-disc space-y-2 pl-5">
                  {activeRole.kpis.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ul>
              </Section>
            ) : null}
            <div className="pt-2">
              <a
                href="#apply"
                onClick={() => setSelectedRoleId(activeRole.id)}
                className="inline-flex rounded-xl bg-slate-900 px-4 py-2 text-white hover:bg-slate-800"
              >
                Apply for this role
              </a>
            </div>
          </div>
        )}
      </Modal>

      {/* Application Form */}
      <section
        id="apply"
        className="mx-auto mt-12 max-w-3xl px-4 sm:px-6 lg:px-8"
      >
        <div className="rounded-2xl border bg-sdbodywhite p-6 shadow-sm">
          <h2 className="text-2xl font-semibold">Apply</h2>
          <p className="mt-2 text-slate-600">
            Complete the short form and upload your CV. We will contact
            shortlisted candidates.
          </p>

          {submitted && (
            <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">
              Thank you. Your application has been received.
            </div>
          )}
          {formError && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
              {formError}
            </div>
          )}

          <form
            action={
              SUBMIT_CONFIG.mode === "formsubmit"
                ? SUBMIT_CONFIG.formsubmitEndpoint
                : undefined
            }
            method={SUBMIT_CONFIG.mode === "formsubmit" ? "POST" : undefined}
            encType={
              SUBMIT_CONFIG.mode === "formsubmit"
                ? "multipart/form-data"
                : undefined
            }
            onSubmit={handleSubmit}
            className="mt-6 space-y-5"
          >
            {SUBMIT_CONFIG.mode === "formsubmit" && (
              <>
                {/* optional: nicer subject line */}
                <input
                  type="hidden"
                  name="_subject"
                  value="New ESBD application"
                />
                {/* optional: thanks page redirect */}
                <input
                  type="hidden"
                  name="_next"
                  value="https://www.eusbd.eu/career"
                />
                {/* recommended: keep captcha ON for spam resistance; set to 'false' to disable */}
                {/* <input type="hidden" name="_captcha" value="false" /> */}
                {/* optional: autoresponse to applicant (requires email field present) */}
                {/* <input type="hidden" name="_autoresponse" value="Thanks! We received your application." /> */}
              </>
            )}

            {/* Q1 Role */}
            <div>
              <label className="mb-1 block text-sm font-medium">Role</label>
              <select
                name="roleId"
                value={form.roleId || selectedRoleId}
                onChange={onChange}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              >
                <option value="" disabled>
                  Select a role
                </option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title}
                  </option>
                ))}
              </select>
            </div>

            {/* Q2 Name */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Full name
              </label>
              <input
                type="text"
                name="fullName"
                value={form.fullName}
                onChange={onChange}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* Q3 Email */}
            <div>
              <label className="mb-1 block text-sm font-medium">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* Q4 Country */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Country of residence
              </label>
              <input
                type="text"
                name="country"
                value={form.country}
                onChange={onChange}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                placeholder="e.g., Malta, Ghana, Kenya, South Africa, Nigeria"
                required
              />
            </div>

            {/* Q5 Years experience */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Years of relevant experience
              </label>
              <input
                type="number"
                min={0}
                max={50}
                name="yearsExp"
                value={form.yearsExp}
                onChange={onChange}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* Q6 Achievement */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Your strongest achievement related to this role (about 200
                words)
              </label>
              <textarea
                name="achievement"
                value={form.achievement}
                onChange={onChange}
                rows={5}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* Q7 Role-specific */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                {roleSpecificPrompt}
              </label>
              <textarea
                name="roleSpecific"
                value={form.roleSpecific}
                onChange={onChange}
                rows={5}
                className="w-full rounded-xl border px-4 py-3 focus:outline-none focus:ring"
                required
              />
            </div>

            {/* CV upload */}
            <div>
              <label className="mb-1 block text-sm font-medium">
                Upload CV (PDF or DOCX)
              </label>
              <input
                type="file"
                name="cv"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={onChange}
                className="block w-full text-sm"
                required
              />
              <p className="mt-2 text-xs text-slate-500">
                By submitting, you consent to ESBD processing your data for
                recruitment. We normally retain applications for up to 12
                months.
              </p>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={formLoading}
                className={classNames(
                  "w-full rounded-xl px-5 py-3 text-white shadow",
                  formLoading
                    ? "bg-slate-400"
                    : "bg-slate-900 hover:bg-slate-800"
                )}
              >
                {formLoading ? "Submitting..." : "Submit application"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="mx-auto mt-12 max-w-6xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl border bg-sdbodywhite p-6 text-sm text-slate-600 shadow-sm">
          <p>
            ESBD is an equal opportunities organisation. We welcome applications
            from all qualified candidates.
          </p>
          <p className="mt-2">
            If you require adjustments for the selection process, email
            careers@eusbd.eu and we will support you.
          </p>
        </div>
      </footer>
    </main>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl border bg-slate-50 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="text-sm text-slate-800">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h4 className="text-base font-semibold">{title}</h4>
      <div className="mt-2 text-slate-700">{children}</div>
    </section>
  );
}
